import { uniqBy } from "lodash";
import { MentionsPlugin } from "../../../sked-editor/social-mention/mention-plugin";
import { isSuggestOptionLiOrg } from "../../utils/autocomplete";
import { $createSkedSocialMentionNode } from "../sked-social-mention";
import { SuggestFunc } from "../types";
import { LocalStorageCache } from "./local-storage-cache";
import { SkedMentionBox, SkedMentionItem } from "./sked-mention-box";
import { PUNCTUATION } from "../../../sked-editor/type-ahead/type-ahead-plugin";
import { PostLinkedInAccountType } from "../../../../shared/types/accounts";
import { useMemo } from "react";
import _ from "lodash";

const liCache = new LocalStorageCache("li-org-mention-cache", 20);
function isPersonalUrn(urn: string | undefined) {
  return urn?.includes("person");
}
export const LiMentionSearchPlugin = ({
  lookupFunc,
  liOrgUrn,
  hasMoreThanOnelinkedInAccount,
  selectedLinkedInAccountType,
}: {
  lookupFunc: SuggestFunc;
  liOrgUrn: string | undefined;
  hasMoreThanOnelinkedInAccount: boolean | undefined;
  selectedLinkedInAccountType: PostLinkedInAccountType | undefined;
}) => {
  const resolvedTooltip = hasMoreThanOnelinkedInAccount
    ? "A personal profile cannot be mentioned when more than one LinkedIn account is selected."
    : selectedLinkedInAccountType === "personal"
      ? "A Personal Profile cannot be mentioned by a Personal Profile."
      : selectedLinkedInAccountType === "company"
        ? "Only followers of the company page can be mentioned."
        : undefined;
  const personalCache = useMemo(() => {
    return new LocalStorageCache(`li-personal-mention-cache-${liOrgUrn}`, 20);
  }, [liOrgUrn]);

  return (
    <MentionsPlugin
      lookupConfig={{
        allowWhitespace: true,
        minLength: 0,
        punctuation: PUNCTUATION.replace(".", ""),
      }}
      onSelectItem={(item) => {
        if (isPersonalUrn(item.key)) {
          personalCache.addItem(item);
        } else {
          liCache.addItem(item);
        }
      }}
      menuBoxBehavior={{
        nodeConversion: {
          type: "mention-node",
          trailingSpace: true,
          convertFunc: (item) => {
            return {
              platformStructureText: `@[${item.key}](${item.displayName})`,
              displayText: item.displayName,
            };
          },
          $createMentionNode: $createSkedSocialMentionNode,
        },

        dataFetcher: async (
          lookupText: string | null | undefined
        ): Promise<SkedMentionItem[]> => {
          if (!lookupText) {
            return [];
          }
          const rs = await lookupFunc(lookupText || "", undefined, liOrgUrn);
          return rs
            .filter((c) => isSuggestOptionLiOrg(c))
            .map(
              (c) =>
                ({
                  key: c.urn,
                  displayName: c.name,
                  profileImg: c.picture,
                  info: isPersonalUrn(c.urn) ? "Personal" : "Company",
                  username: c.vanityName,
                }) satisfies SkedMentionItem as SkedMentionItem
            );
        },
        dataResolver: (
          items: SkedMentionItem[],
          lookupText: string | null | undefined
        ) => {
          const cacheRs = _.orderBy(
            [...liCache.getItems(), ...personalCache.getItems()],
            [(c) => c._t || 1],
            ["desc"]
          );
          return uniqBy(
            [
              ...items,
              ...cacheRs.filter((c) =>
                (isPersonalUrn(c.key)
                  ? c.displayName || ""
                  : c.username || c.displayName || ""
                )
                  .toLowerCase()
                  .includes((lookupText || "").toLowerCase())
              ),
            ],
            (c) => c.key
          );
        },

        renderMenuBox: (props, { lookupText }) => {
          return (
            <SkedMentionBox
              {...props}
              headingText="Search accounts"
              infoTooltip={resolvedTooltip}
              displayConfig={{
                showProfileImage: true,
                showInfo: true,
                showUsername: true,
              }}
              highlightBehavior={{
                matchingOn: "contains",
                searchText: lookupText,
              }}
            />
          );
        },

        maxItemsToShow: 20,
      }}
    />
  );
};
